if (!customElements.get('filazoo-discover-more')) {
  customElements.define(
    'filazoo-discover-more',
    class FilazooDiscoverMore extends HTMLElement {
      connectedCallback() {
        // The recommendations render arrives with its cards already in place.
        if (this.querySelector('[data-discover-card]')) {
          this.initCards();
          return;
        }

        if (this.loaded) return;

        // This element is empty until the recommendations arrive, which makes it a
        // zero-area target — and a zero-area target never reports as intersecting, so
        // an IntersectionObserver here would wait forever. Load when the page goes
        // idle instead: off the critical path, but not dependent on layout.
        if ('requestIdleCallback' in window) {
          this.idleHandle = requestIdleCallback(() => this.load(), { timeout: 3000 });
        } else {
          this.timeoutHandle = setTimeout(() => this.load(), 1000);
        }
      }

      disconnectedCallback() {
        if (this.idleHandle && 'cancelIdleCallback' in window) cancelIdleCallback(this.idleHandle);
        if (this.timeoutHandle) clearTimeout(this.timeoutHandle);
      }

      load() {
        this.loaded = true;
        const { url, productId, sectionId } = this.dataset;

        fetch(`${url}&product_id=${productId}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const source = html.querySelector('filazoo-discover-more');

            // No complementary products for this product: nothing to show.
            if (!source || !source.querySelector('[data-discover-card]')) {
              this.remove();
              return;
            }

            this.innerHTML = source.innerHTML;
            this.classList.add('filazoo-discover-more--loaded');
            this.initCards();
          })
          .catch((error) => {
            console.error(error);
            this.remove();
          });
      }

      initCards() {
        this.querySelectorAll('[data-discover-card]').forEach((card) => {
          if (card.dataset.discoverReady === 'true') return;
          card.dataset.discoverReady = 'true';
          new FilazooDiscoverCard(card);
        });
      }
    }
  );
}

class FilazooDiscoverCard {
  constructor(card) {
    this.card = card;
    this.select = card.querySelector('[data-discover-variant-select]');
    this.input = card.querySelector('[data-discover-variant-input]');
    this.submit = card.querySelector('[data-discover-submit]');
    this.submitText = card.querySelector('[data-discover-submit-text]');
    this.price = card.querySelector('[data-discover-price]');
    this.compare = card.querySelector('[data-discover-compare]');
    this.badge = card.querySelector('[data-discover-badge]');
    this.badgeText = card.querySelector('[data-discover-badge-text]');
    this.image = card.querySelector('img.filazoo-discover-card__image');
    this.variants = this.parseVariants();

    if (!this.select || !this.variants.length) return;
    this.select.addEventListener('change', () => this.onVariantChange());
  }

  parseVariants() {
    const script = this.card.querySelector('[data-discover-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent) || [];
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  onVariantChange() {
    const variant = this.variants.find((item) => String(item.id) === this.select.value);
    if (!variant) return;

    if (this.input) this.input.value = variant.id;
    if (this.price) this.price.textContent = variant.price;

    if (this.compare) {
      this.compare.textContent = variant.compare_at_price || '';
      this.compare.hidden = !variant.compare_at_price;
    }

    if (this.badge && this.badgeText) {
      this.badgeText.textContent = `${variant.discount}% off`;
      this.badge.hidden = !variant.discount;
    }

    if (this.image && variant.image) {
      this.image.removeAttribute('srcset');
      this.image.src = variant.image;
    }

    if (this.submit) {
      this.submit.disabled = !variant.available;
      this.submit.removeAttribute('aria-disabled');
      if (this.submitText) {
        this.submitText.textContent = variant.available
          ? this.submit.dataset.addLabel || 'Add'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }
  }
}
